/**
 * ProjectStart — start a project.
 *
 * Bathroom: (1) "How many bathrooms?" → (2) name the overall project + name each
 * bathroom → create the project and enter the loop. Each bathroom is then named
 * again for confirmation at the top of its question loop (DesignFlow).
 *
 * Kitchen / paint: a single "Name your project" screen, then straight into the
 * questions (single pass; paint "both" carries fixed interior/exterior passes).
 */
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createProject } from '../../lib/api';

interface StartState {
  serviceLine?: string;
  productCode?: string;
  /** The master (property) this project belongs to. */
  masterId?: string;
  /** Per-room service lines/product codes when rooms differ (paint both). */
  fixedServiceLines?: string[];
  fixedProductCodes?: string[];
  /** Labels for a fixed multi-pass project (paint interior + exterior). */
  fixedLabels?: string[];
}

const MAX_BATHROOMS = 6;

function defaultName(serviceLine: string): string {
  switch (serviceLine) {
    case 'BATHROOM':
      return 'Bathroom remodel';
    case 'KITCHEN':
      return 'Kitchen remodel';
    case 'PAINT_INT':
      return 'Interior paint';
    case 'PAINT_EXT':
      return 'Exterior paint';
    default:
      return 'My project';
  }
}

export function ProjectStart(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as StartState) ?? {};
  const serviceLine = state.serviceLine ?? 'BATHROOM';
  const productCode = state.productCode ?? 'BATH_FULL';
  const fixed = state.fixedLabels;
  const isBathroom = serviceLine === 'BATHROOM' && !fixed;

  // Start blank; the default shows as a placeholder and is used only if left empty.
  // (Pre-filling the field led to customers typing after the default and getting
  // names like "Mutiple Bathrooms Bathroom remodel".)
  const [name, setName] = useState<string>('');
  const placeholderName = fixed ? 'Interior & exterior paint' : defaultName(serviceLine);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bathroom-only: count + per-bathroom labels, gathered before the project is made.
  const [bathStep, setBathStep] = useState<'count' | 'name'>('count');
  const [count, setCount] = useState(1);
  const [bathLabels, setBathLabels] = useState<string[]>(['']);

  const bathroomLabels = useMemo<string[]>(
    () => Array.from({ length: count }, (_, i) => bathLabels[i]?.trim() || `Bathroom ${i + 1}`),
    [count, bathLabels],
  );

  function setCountSafe(n: number): void {
    const c = Math.max(1, Math.min(MAX_BATHROOMS, n));
    setCount(c);
    setBathLabels((prev) => {
      const next = prev.slice(0, c);
      while (next.length < c) next.push('');
      return next;
    });
  }

  async function begin(roomLabels: string[]): Promise<void> {
    const projectName = name.trim() || placeholderName;
    setBusy(true);
    setError(null);
    try {
      const projectId = await createProject({
        ...(state.masterId ? { masterId: state.masterId } : {}),
        name: projectName,
      });
      navigate('/design/flow', {
        state: {
          serviceLine,
          productCode,
          projectId,
          projectName,
          roomLabels,
          roomIndex: 0,
          fresh: true,
          ...(state.fixedServiceLines ? { roomServiceLines: state.fixedServiceLines } : {}),
          ...(state.fixedProductCodes ? { roomProductCodes: state.fixedProductCodes } : {}),
        },
      });
    } catch {
      setError('We couldn\u2019t start your project just now. Try again in a moment.');
      setBusy(false);
    }
  }

  // ---- Bathroom: step 1 — how many bathrooms? ------------------------------
  if (isBathroom && bathStep === 'count') {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <h1>How many bathrooms are we quoting?</h1>
          <p className="stage-card__lede">
            We&rsquo;ll scope each one on its own, then put them under a single project. One free
            estimate covers the whole visit.
          </p>
          <div className="pb-surface" style={{ display: 'flex', alignItems: 'center', gap: 'var(--pb-3)' }}>
            <button
              type="button"
              className="pb-btn pb-btn--ghost"
              onClick={() => setCountSafe(count - 1)}
              aria-label="One fewer bathroom"
              disabled={count <= 1}
            >
              &minus;
            </button>
            <span style={{ fontFamily: 'var(--pb-font-display)', fontSize: 'var(--pb-size-large)', minWidth: '2ch', textAlign: 'center' }}>
              {count}
            </span>
            <button
              type="button"
              className="pb-btn pb-btn--ghost"
              onClick={() => setCountSafe(count + 1)}
              aria-label="One more bathroom"
              disabled={count >= MAX_BATHROOMS}
            >
              +
            </button>
          </div>
          <div className="q-actions">
            <button type="button" className="pb-btn pb-btn--lg pb-btn--next" onClick={() => setBathStep('name')}>
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Bathroom: step 2 — name the project + each bathroom -----------------
  if (isBathroom) {
    return (
      <div className="flow flow--single">
        <div className="stage-card">
          <h1>Name your project</h1>
          <p className="stage-card__lede">
            Give the whole job a name, then a name for each bathroom so you can tell them apart.
          </p>

          <div style={{ marginTop: 'var(--pb-3)' }}>
            <label className="pb-label" htmlFor="project-name">Project name</label>
            <input
              id="project-name"
              className="pb-field"
              maxLength={80}
              value={name}
              placeholder={placeholderName}
              onChange={(e) => setName(e.target.value)}
              aria-label="Project name"
            />
          </div>

          <div style={{ marginTop: 'var(--pb-4)' }}>
            <div className="progress__count" style={{ marginBottom: 'var(--pb-2)' }}>
              NAME EACH BATHROOM (OPTIONAL)
            </div>
            {Array.from({ length: count }, (_, i) => (
              <input
                key={i}
                type="text"
                className="pb-field"
                maxLength={60}
                placeholder={`Bathroom ${i + 1}`}
                value={bathLabels[i] ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setBathLabels((prev) => {
                    const next = [...prev];
                    next[i] = v;
                    return next;
                  });
                }}
                style={{ marginBottom: 'var(--pb-2)' }}
                aria-label={`Name for bathroom ${i + 1}`}
              />
            ))}
          </div>

          {error ? (
            <div className="pb-note pb-note--flag" role="alert">{error}</div>
          ) : null}

          <div className="q-actions" style={{ flexDirection: 'column' }}>
            <button
              type="button"
              className="pb-btn pb-btn--lg"
              onClick={() => void begin(bathroomLabels)}
              disabled={busy}
            >
              {busy ? 'Starting\u2026' : `Start with ${bathroomLabels[0]}`}
            </button>
            <button type="button" className="pb-btn pb-btn--ghost" onClick={() => setBathStep('count')} disabled={busy}>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Kitchen / paint: single "name your project" -------------------------
  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <h1>Name your project</h1>
        <p className="stage-card__lede">
          Give it a name you&rsquo;ll recognise later &mdash; like &ldquo;Kitchen remodel&rdquo; or
          &ldquo;Whole-house interior paint.&rdquo;
        </p>

        <div style={{ marginTop: 'var(--pb-3)' }}>
          <label className="pb-label" htmlFor="project-name">Project name</label>
          <input
            id="project-name"
            className="pb-field"
            maxLength={80}
            value={name}
            placeholder={placeholderName}
            onChange={(e) => setName(e.target.value)}
            aria-label="Project name"
          />
        </div>

        {error ? (
          <div className="pb-note pb-note--flag" role="alert">{error}</div>
        ) : null}

        <div className="q-actions">
          <button
            type="button"
            className="pb-btn pb-btn--lg"
            onClick={() => void begin(fixed ?? [name.trim() || placeholderName])}
            disabled={busy}
          >
            {busy ? 'Starting\u2026' : 'Start'}
          </button>
        </div>
      </div>
    </div>
  );
}
